import { CUSTOMER_STATUSES, customerStatusSchema } from '@avrahami/contracts';
import {
  Alert,
  Button,
  Card,
  Cell,
  formatDate,
  LoadMore,
  Loading,
  PageHeader,
  SelectField,
  Table,
  TextField,
  useListFilters,
} from '@avrahami/ui';
import { useInfiniteQuery } from '@tanstack/react-query';
import { Link } from '@tanstack/react-router';
import type { SyntheticEvent } from 'react';
import { customerListQuery, type CustomerFilters, useStoreTimeZone } from '../api/tenant';
import { CustomerStatusBadge } from '../components/status';
import { he } from '../i18n/he';
import { errorMessage } from '../lib/forms';

const NO_FILTERS: CustomerFilters = { q: '', status: '' };

export function CustomersPage() {
  const { draft, filters, change, apply, reset } = useListFilters('customers', NO_FILTERS);
  const customers = useInfiniteQuery(customerListQuery(filters));
  const timeZone = useStoreTimeZone();
  const items = customers.data?.pages.flatMap((page) => page.items) ?? [];

  const onFilter = (event: SyntheticEvent) => {
    event.preventDefault();
    apply();
  };

  return (
    <>
      <PageHeader title={he.customers.title} />
      <Card>
        <form onSubmit={onFilter} className="mb-4 flex flex-wrap items-end gap-3">
          <TextField
            label={he.customers.search}
            name="q"
            type="search"
            className="w-72"
            maxLength={100}
            value={draft.q}
            onChange={(event) => {
              change({ q: event.target.value });
            }}
          />
          <SelectField
            label={he.customers.status}
            name="status"
            value={draft.status}
            onChange={(event) => {
              const value = event.target.value;
              change({ status: value === '' ? '' : customerStatusSchema.parse(value) });
            }}
          >
            <option value="">{he.common.all}</option>
            {CUSTOMER_STATUSES.map((status) => (
              <option key={status} value={status}>
                {he.customerStatus[status]}
              </option>
            ))}
          </SelectField>
          <Button type="submit" tone="secondary">
            {he.common.search}
          </Button>
          <Button
            tone="secondary"
            onClick={() => {
              reset();
            }}
          >
            {he.common.clear}
          </Button>
        </form>
        {customers.isPending ? <Loading /> : null}
        {customers.isError ? <Alert>{errorMessage(customers.error)}</Alert> : null}
        {customers.isSuccess && items.length === 0 ? (
          <p className="text-sm text-slate-500">{he.customers.empty}</p>
        ) : null}
        {items.length > 0 ? (
          <Table
            head={[
              he.customers.name,
              he.customers.email,
              he.customers.phone,
              he.customers.status,
              he.customers.orders,
              he.customers.lastOrder,
            ]}
          >
            {items.map((customer) => (
              <tr key={customer.id} data-testid="customer-row" data-email={customer.email}>
                <Cell>
                  <Link
                    to="/customers/$customerId"
                    params={{ customerId: customer.id }}
                    className="font-medium text-brand-700 hover:underline"
                  >
                    {customer.fullName}
                  </Link>
                </Cell>
                <Cell className="ltr">{customer.email}</Cell>
                <Cell className="ltr">{customer.phone ?? he.common.none}</Cell>
                <Cell>
                  <CustomerStatusBadge status={customer.status} />
                </Cell>
                <Cell>{customer.orderCount}</Cell>
                <Cell>
                  {customer.lastOrderAt === null
                    ? he.common.none
                    : formatDate(customer.lastOrderAt, timeZone)}
                </Cell>
              </tr>
            ))}
          </Table>
        ) : null}
        <LoadMore
          hasMore={customers.hasNextPage}
          busy={customers.isFetchingNextPage}
          onMore={() => {
            void customers.fetchNextPage();
          }}
        />
      </Card>
    </>
  );
}
